<template>
  <div class="custom-task-edit-component">
    <div class="custom-task-wrapper" v-if="!isLoading">
      <div class="code-tool-wrapper">
        <div class="task-tools">
          <ul class="left-side-options">
            <li class="run-code" v-b-tooltip.hover title="Ausführen">
              <a v-if="!loading" @click="runCode">
                <i class="fas fa-play"></i>
                <span class="accessibility">Run</span>
              </a>
              <b-spinner
                v-else
                class="call-spinner"
                label="Spinning"
              ></b-spinner>
            </li>
          </ul>
          <ul class="right-side-options">
            <li
              class="save-task"
              v-b-tooltip.hover
              :title="isEditMode ? 'Aktualisieren' : 'Speichern'"
            >
              <a @click="handleSave">
                <i class="fa fa-save"></i>
              </a>
            </li>
            <li
              class="code-sign"
              v-b-tooltip.hover
              :title="isCodeEditorOpen ? 'Zurück zu Metadaten' : 'Code'"
              @click="toggleCodeEditor"
            >
              <a>
                <i :class="isCodeEditorOpen ? 'fa fa-list' : 'fa fa-code'"></i>
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div class="content">
        <form
          v-if="!isCodeEditorOpen"
          @submit.prevent="createCustomTask()"
          ref="customTaskForm"
          class="make-custom-task-wrap"
        >
          <div class="icon-picker-wrapper input-attributes">
            <label>Symbol</label>
            <div class="icon-content-wrap">
              <div v-if="taskDetails.icon" class="image-preview">
                <img :src="taskDetails.icon" />
              </div>
              <div
                v-click-outside="toggleIconPickerURL"
                class="icon-picker-url-selector"
              >
                <div
                  class="btn-black mb-0"
                  @click.stop="toggleIconPickerURL(true)"
                >
                  <span> Bild hinzufügen </span>
                </div>

                <IconPicker
                  v-if="isIconPickerOpen"
                  :displayIconPickerSections="[
                    'icon',
                    'illustration',
                    'custom',
                  ]"
                  :element="taskDetails"
                  @updateUrl="taskEditorUpdateIconUrl"
                  @updateLinkType="taskEditorUpdateIconUrlType"
                />
              </div>
            </div>
          </div>
          <div class="input-attributes">
            <label>Aufgabenname</label>
            <input v-model="taskDetails.taskname" required />
          </div>
          <div class="input-attributes">
            <label>Aufgabenbeschreibung</label>
            <textarea
              class="description-input"
              placeholder="Description"
              v-model="taskDetails.description"
            />
          </div>
          <div class="input-attributes radio-input-attributes">
            <label class="field-label">Task element größe</label>
            <div class="radio-options">
              <label class="radio-option">
                <input
                  type="radio"
                  value="Klein"
                  v-model="taskDetails.tasksize"
                />
                <span>Klein</span>
              </label>
              <label class="radio-option">
                <input
                  type="radio"
                  value="Mittle"
                  v-model="taskDetails.tasksize"
                />
                <span>Mittle</span>
              </label>
            </div>
          </div>
          <div
            class="variables-section"
            v-if="variableInputFields && variableInputFields.length"
          >
            <div class="variables-header">
              <h3>Variablenbezeichnungen</h3>
            </div>
            <div class="variables-list">
              <div
                class="variable-row"
                v-for="field in variableInputFields"
                :key="field.name"
              >
                <span class="variable-name">{{ field.name }}</span>
                <input
                  type="text"
                  class="variable-label-input"
                  :value="field.label"
                  @input="handleLabelChange(field, $event.target.value)"
                  :placeholder="field.label || field.name"
                />
              </div>
            </div>
          </div>
        </form>
        <div v-else class="custom-code-modal-content-wrapper">
          <div class="custom-task-detail tab">
            <div class="code-editors-container">
              <div
                class="editor-wrapper dragable-wrapper read-only"
                ref="topTextArea"
                id="codeEditor1"
              >
                <span
                  class="dragable-line-first"
                  @mousedown="mouseDownAtEditor($event, 'codeEditor1')"
                ></span>
                <prism-editor
                  ref="editor-read-only-input"
                  class="code-editor"
                  v-model="inputAsCode"
                  :highlight="highlighter"
                  :readonly="true"
                  line-numbers
                ></prism-editor>
              </div>
              <div
                class="editor-wrapper read-write"
                ref="middleTextArea"
                id="codeEditor2"
              >
                <prism-editor
                  ref="editor"
                  class="code-editor"
                  v-model="taskDetails.code"
                  :highlight="highlighter"
                  :readonly="false"
                  line-numbers
                ></prism-editor>
              </div>
              <div
                ref="bottomTextArea"
                class="editor-wrapper read-only"
                :class="{ 'hide-overflow': loading }"
                id="codeEditor3"
              >
                <span
                  class="dragable-line-second"
                  @mousedown="mouseDownAtEditor($event, 'codeEditor3')"
                ></span>
                <prism-editor
                  ref="editor-read-only-output"
                  class="code-editor"
                  v-model="outputAsCode"
                  :highlight="highlighter"
                  :readonly="true"
                  line-numbers
                ></prism-editor>
                <div class="loading" v-show="loading">
                  <div class="progress-icon-wrapper">
                    <div class="progress-icon"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <LoadingSpinner loadingCaption="Wird gespeichert..." v-else />
  </div>
</template>

<script>
import IconPicker from './IconPicker.vue';
import LoadingSpinner from './LoadingSpinner.vue';
import { mapGetters } from 'vuex';
import {
  formatTablename,
  getCETTimeStampFromDate,
  getApiEndpointVersion,
  jsonParser,
  getFileExtension,
  getFileNameWithoutExtension,
} from '../../utils/helpers';
import { createDatabaseTable } from '../../services/customdb.service';
import customTaskDatabaseSchema from '../../assets/data-schemas/customTaskDatabaseSchema.json';
import { addTableItem, updateTableItem } from '../../services/userTable';
import { askChatGPT } from '../../services/workflow.service';
import { extractInputValuesFromCode } from '../../utils/helpers/WorkFlowHelpers';
import { getCustomDatabaseItem } from '../../services/axios/data.api';
import { PrismEditor } from 'vue-prism-editor';
import 'vue-prism-editor/dist/prismeditor.min.css';
import { highlight, languages } from 'prismjs/components/prism-core';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-python';
import 'prismjs/themes/prism-tomorrow.css';
export default {
  name: 'MakeCustomTaskComponent',
  data() {
    return {
      isLoading: false,
      loading: false,
      isCodeEditorOpen: false,
      currentEditor: null,
      m_pos: null,
      labelOverrides: {},
      taskDetails: {
        description: null,
        taskname: null,
        icon: null,
        linkType: null,
        tasksize: 'Klein',
        code: null,
      },

      customTaskDetails: null,
      isIconPickerOpen: false,
      jsonTypes: ['object', 'objectlist', 'model', 'json'],
      modelTypes: [
        'timeseries', // timeseries is needed for backwards compatiblility
        'loadprofile',
        'custom',
      ],
      listTypes: ['checkbox'],
    };
  },
  props: {
    editCodeDetails: {
      type: Object,
      default: null,
      required: true,
    },
    inputFields: {
      type: Array,
      default: null,
      required: true,
    },
    flowRunnerEndpoint: {
      type: String,
    },
    process: {
      type: Object,
      default: null,
    },
    customTask: {
      type: Object,
      default: null,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
    task: Object,
  },
  methods: {
    close() {
      this.$emit('closeEditModal', false);
    },

    toggleIconPickerURL(value = false) {
      this.isIconPickerOpen = value;
    },
    taskEditorUpdateIconUrl(icon) {
      this.taskDetails.icon = icon;
      this.toggleIconPickerURL();
    },
    taskEditorUpdateIconUrlType(type) {
      this.taskDetails.linkType = type;
      this.toggleIconPickerURL();
    },
    async writeMessage(code) {
      this.isLoading = true;
      const promptMessage = `Give me short description in german about this code
      IMPORTANT : make sure to give me description with normal natural language and not include code

      code:
      `;
      try {
        const url = this.flowRunnerEndpoint;
        let res;
        if (getApiEndpointVersion(url) >= 6) {
          const formData = new FormData();
          formData.append(
            'props',
            JSON.stringify({
              prompt: promptMessage + code,
              model: 'gpt-4',
            })
          );
          res = await askChatGPT(url, formData);
        } else {
          res = await askChatGPT(url, {
            prompt: promptMessage + code,
          });
        }
        this.taskDetails.description = res.data.message;
        this.isLoading = false;
      } catch (error) {
        this.isLoading = false;
        throw new Error(error);
      }
    },
    onTypeChange(selectType, name) {
      this.inputFieldsDetails.map((data) => {
        if (data.name == name) data.type = selectType;
      });
    },
    async createCustomTask() {
      if (this.isEditMode) {
        this.updateCustomTask();
        return;
      }
      this.isLoading = true;
      let customtaskDatabaseDetails = this.databaseList.find(
        (database) => database.type == 'custom-task'
      );
      if (!customtaskDatabaseDetails)
        customtaskDatabaseDetails = await this.createCustomTaskDatabase();
      this.addCustomTaskItemInDatabase(
        customtaskDatabaseDetails,
        this.taskDetails
      );
    },
    async createCustomTaskDatabase() {
      try {
        const databaseSchema = customTaskDatabaseSchema.map(
          (schema, index) => ({
            ...schema,
            id: getCETTimeStampFromDate() + (index + 1),
          })
        );
        const formData = new FormData();
        formData.append('caption', 'custom-task');
        formData.append('add_only', false);
        formData.append('icon', null);
        formData.append(
          'tablename',
          this.getUsername + '_' + formatTablename('custom-task')
        );
        formData.append('tabletype', 'custom-task');
        formData.append(
          'layout',
          '{"tabs":[{"id":0,"rows":[{"id":0,"cols":[{"id":0}]}]}]}'
        );
        formData.append('database_type', 'DynamoDB');
        formData.append('type', 'custom-task');
        formData.append('schema', JSON.stringify(databaseSchema));

        const createDatabaseResponse = await createDatabaseTable(formData);
        await this.$store.dispatch('fetchUserDataBase');
        return JSON.parse(createDatabaseResponse.data);
      } catch (error) {
        console.log('error :>> ', error);
      }
    },
    async addCustomTaskItemInDatabase(database, taskDetails) {
      const payloadData = JSON.parse(JSON.stringify(taskDetails));
      delete payloadData.linkType;
      delete payloadData.code;
      const filterUsedInputs = this.createInputsFromCode(this.taskDetails.code);
      const payload = {
        item: {
          ...payloadData,
          code: { ...this.editCodeDetails, code: this.taskDetails.code },
          inputfields: filterUsedInputs,
        },
      };
      try {
        const response = await addTableItem(database.name, payload);
        if (response.status == 200) {
          this.isLoading = false;
          this.$emit('showToaster', 'Ausgeführt !', 'Erfolgreich', 'success');
          this.$emit('triggerCustomTaskItemFetch', response.data.item_id);
          this.$emit('closeEditModal', true);
        }
      } catch (error) {
        this.isLoading = false;
        this.$emit(
          'showToaster',
          'Bitte beheben Sie den Fehler im Code !',
          'Fehlerhaft !',
          'danger'
        );
      }
    },
    createInputsFromCode(code) {
      const inputString = code;
      if (!inputString) return [];

      const extractAllInputs = extractInputValuesFromCode(inputString);

      return Object.entries(extractAllInputs).map(([key, obj]) => {
        const found =
          this.task?.props?.inputs?.find((e) => e.name === key) ||
          this.inputFields?.find((e) => e.name === key);

        if (found)
          return {
            ...found,
            label: this.getInputLabel(key, found.label || key),
          };

        return {
          label: this.getInputLabel(key, key),
          name: key,
          value: key === 'authorization' ? '' : obj.value,
          type: obj.type,
        };
      });
    },
    async updateCustomTask() {
      try {
        if (!this.customTaskDetails) return;
        this.isLoading = true;
        const isChangeProperty =
          !(
            this.customTaskDetails.description ==
              this.taskDetails.description &&
            this.customTaskDetails.taskname == this.taskDetails.taskname &&
            this.customTaskDetails.icon == this.taskDetails.icon &&
            this.customTaskDetails.tasksize == this.taskDetails.tasksize &&
            this.customTaskDetails.code.code == this.taskDetails.code
          ) ||
          JSON.stringify(
            (this.task.props.inputs || []).map((field) => ({
              name: field.name,
              label: field.label,
            }))
          ) !==
            JSON.stringify(
              (this.variableInputFields || []).map((field) => ({
                name: field.name,
                label: field.label,
              }))
            );

        this.customTaskDetails.description = this.taskDetails.description;
        this.customTaskDetails.taskname = this.taskDetails.taskname;
        this.customTaskDetails.icon = this.taskDetails.icon;
        this.customTaskDetails.tasksize = this.taskDetails.tasksize;
        this.customTaskDetails.code.code = this.taskDetails.code;
        const inputString = this.taskDetails.code;
        const extractAllInputs = extractInputValuesFromCode(inputString);

        const filterUsedInputs = Object.entries(extractAllInputs).map(
          ([key, obj]) => {
            const existingInput = this.task?.props?.inputs?.find(
              (e) => e.name === key
            );
            if (existingInput)
              return {
                ...existingInput,
                label: this.getInputLabel(key, existingInput.label || key),
              };
            const found = this.inputFields.find((e) => e.name === key);

            if (found)
              return {
                ...found,
                label: this.getInputLabel(key, found.label || key),
              };

            return {
              label: this.getInputLabel(key, key),
              name: key,
              value: key === 'authorization' ? '' : obj.value,
              type: obj.type,
            };
          }
        );

        const response = isChangeProperty
          ? await updateTableItem(this.getCustomTaskDatabase[0].name, {
              item: {
                ...this.customTaskDetails,
                inputfields: filterUsedInputs,
              },
            })
          : { status: 200 };
        if (response.status == 200) {
          this.customTaskDetails.inputfields = filterUsedInputs;
          this.$set(this.task, 'caption', this.customTaskDetails.taskname);
          this.$set(this.task, 'customIcon', this.taskDetails.icon);
          this.$set(this.task, 'props', {
            ...this.task.props,
            description: this.customTaskDetails.description,
            name: this.customTaskDetails.taskname,
            tasksize: this.customTaskDetails.tasksize,
            code: this.taskDetails.code,
            inputs: filterUsedInputs,
          });
          this.$emit(
            'updateCustomTaskDetails',
            JSON.parse(JSON.stringify(this.customTaskDetails))
          );
          this.$emit('triggerCustomTaskItemFetch', this.customTaskDetails.id);
          this.$emit('showToaster', 'aktualisiert !', 'Erfolgreich', 'success');
          this.$emit('closeEditModal', true);
        }
      } catch (error) {
        console.log('error :>> ', error);
        this.$emit(
          'showToaster',
          'Bitte beheben Sie den Fehler im Code !',
          'Fehlerhaft !',
          'danger'
        );
      } finally {
        this.isLoading = false;
      }
    },
    async setCustomTaskDetails() {
      try {
        this.isLoading = true;
        if (!this.customTask) {
          if (!this.task?.props?.customTaskId) return;
          const response = await getCustomDatabaseItem(
            this.getCustomTaskDatabase[0].name,
            this.task?.props?.customTaskId
          );
          this.customTaskDetails = response.data;
        } else {
          this.customTaskDetails = JSON.parse(JSON.stringify(this.customTask));
        }
        this.taskDetails = {
          description: this.customTaskDetails?.description,
          tasksize: this.customTaskDetails?.tasksize,
          taskname: this.customTaskDetails?.taskname,
          icon: this.customTaskDetails.icon || this?.task?.customIcon,
          linkType: '',
          code: this.editCodeDetails.code,
        };
      } catch (error) {
        console.log('error :>> ', error);
      } finally {
        this.isLoading = false;
      }
    },
    highlighter(code) {
      return highlight(code, languages.py);
    },
    handleSave() {
      if (this.isLoading || this.loading) return;
      const form = this.$refs.customTaskForm;
      if (form && typeof form.reportValidity === 'function') {
        const isValid = form.reportValidity();
        if (!isValid) return;
      }
      this.createCustomTask();
    },
    handleLabelChange(field, value) {
      this.$set(field, 'label', value);
      this.$set(this.labelOverrides, field.name, value);

      if (this.customTaskDetails?.inputfields) {
        const customField = this.customTaskDetails.inputfields.find(
          (input) => input.name === field.name
        );
        if (customField) this.$set(customField, 'label', value);
      }
    },
    getInputLabel(name, fallback) {
      if (this.labelOverrides?.[name]) return this.labelOverrides[name];
      const sources = [
        this.customTaskDetails?.inputfields,
        this.task?.props?.inputs,
        this.inputFields,
      ];

      for (const list of sources) {
        if (Array.isArray(list)) {
          const found = list.find((input) => input.name === name);
          if (found?.label) return found.label;
        }
      }

      return fallback || name;
    },
    toggleCodeEditor() {
      this.isCodeEditorOpen = !this.isCodeEditorOpen;
      if (!this.isCodeEditorOpen && this.isEditMode) {
        this.customTaskDetails.inputfields = this.createInputsFromCode(
          this.taskDetails.code
        );
      }
      if (!this.isCodeEditorOpen && this.$refs.bottomTextArea) {
        this.$refs.bottomTextArea.style.height = 150 + 'px';
      }
    },
    mouseDownAtEditor(e, editor) {
      e.preventDefault();
      this.currentEditor = editor;
      this.m_pos = e.y;
      document.addEventListener('mousemove', this.resizeCodeEditor, false);
    },
    textAreaHeight() {
      return {
        topPanel: this.$refs.topTextArea.getBoundingClientRect().height,
        middlePanel: this.$refs.middleTextArea.getBoundingClientRect().height,
        bottomPanel: this.$refs.bottomTextArea.getBoundingClientRect().height,
      };
    },
    resizeCodeEditor(e) {
      const panelsHeight = this.textAreaHeight();
      window.addEventListener('mouseup', this.mouseUpAtEditor, true);
      this.m_pos = e.y;

      if (this.currentEditor === 'codeEditor1') {
        const topPanelNewHeight = panelsHeight.topPanel + e.movementY;
        const middlePanelNewHeight = panelsHeight.middlePanel - e.movementY;
        const bottomPanelNewHeight = panelsHeight.bottomPanel - e.movementY;
        if (topPanelNewHeight > 2 && middlePanelNewHeight > 150) {
          this.$refs.topTextArea.style.height = topPanelNewHeight + 'px';
          this.$refs.middleTextArea.style.height = middlePanelNewHeight + 'px';
        }

        if (middlePanelNewHeight < 155 && bottomPanelNewHeight > 150) {
          this.$refs.topTextArea.style.height = topPanelNewHeight + 'px';
          this.$refs.bottomTextArea.style.height = bottomPanelNewHeight + 'px';
        }
      }

      if (this.currentEditor === 'codeEditor3') {
        const topPanelNewHeight = panelsHeight.topPanel + e.movementY;
        const bottomPanelNewHeight = panelsHeight.bottomPanel - e.movementY;
        const middlePanelNewHeight = panelsHeight.middlePanel + e.movementY;

        if (bottomPanelNewHeight > 2 && middlePanelNewHeight > 150) {
          this.$refs.bottomTextArea.style.height = bottomPanelNewHeight + 'px';
          this.$refs.middleTextArea.style.height = middlePanelNewHeight + 'px';
        }

        if (topPanelNewHeight > 150 && middlePanelNewHeight < 155) {
          this.$refs.topTextArea.style.height = topPanelNewHeight + 'px';
          this.$refs.bottomTextArea.style.height = bottomPanelNewHeight + 'px';
        }
      }
    },
    mouseUpAtEditor() {
      document.removeEventListener('mousemove', this.resizeCodeEditor, false);
      window.removeEventListener('mouseup', this.mouseUpAtEditor, true);
    },
    showToaster(message, title, variant) {
      this.$emit('showToaster', message, title, variant);
    },
    setValidValue(key, item, formData, isCheck = false) {
      if (!item) return;
      if (item.type == 'file') formData.append(key, item.value);
      else if (this.jsonTypes.includes(item.type.toLowerCase())) {
        formData.append(
          key,
          typeof item.value === 'string'
            ? item.value
            : JSON.stringify(item.value)
        );
      } else if (this.modelTypes.includes(item.type)) {
        formData.append(key, item?.value?.id);
      } else if (this.listTypes.includes(item.type)) {
        if (isCheck && formData.has(key)) return;
        const parseJsonData = jsonParser(item.value);
        const validArray = Array.isArray(item.value)
          ? item.value
          : typeof item.value === 'string'
          ? Array.isArray(parseJsonData)
            ? parseJsonData
            : item.value.split(',')
          : [item.value];

        validArray.forEach((el) => formData.append(key, el));
      } else {
        formData.append(key, item.value);
      }
    },
    runCode() {
      if (!this.task || !this.process) {
        this.showToaster(
          'Task oder Prozessdaten fehlen für Run Code.',
          'Fehlerhaft !',
          'danger'
        );
        return;
      }
      if (!this.flowRunnerEndpoint) {
        this.showToaster(
          'Flow Runner Endpoint ist nicht definiert.',
          'Fehlerhaft !',
          'danger'
        );
        return;
      }
      const customCodeDetails = this.customCodeDetails;
      if (!customCodeDetails) {
        this.showToaster(
          'Keine Code-Details vorhanden.',
          'Fehlerhaft !',
          'danger'
        );
        return;
      }
      this.loading = true;

      let flowCopy = JSON.parse(JSON.stringify(this.process));
      flowCopy.tasks = [flowCopy.tasks[0]];
      flowCopy.tasks[0].output = [];

      const formData = new FormData();

      this.task.props = customCodeDetails;
      const taskProps = JSON.parse(JSON.stringify(customCodeDetails));
      delete taskProps.inputs;
      formData.append('flow', JSON.stringify(flowCopy));
      formData.append('props', JSON.stringify(taskProps));
      formData.append('task-id', this.task.id);

      const myHeaders = new Headers();
      myHeaders.append(
        'Authorization',
        'Bearer ' + this.user.signInUserSession.accessToken.jwtToken
      );

      if (this.task.input) {
        this.task.input.forEach((e) => {
          this.setValidValue(e.name, e, formData, true);
        });
      }

      const url = `${this.flowRunnerEndpoint}/run_code`;

      fetch(url, {
        body: formData,
        headers: myHeaders,
        method: 'post',
      })
        .then(async (response) => {
          if (response.status == 500) {
            let jsn = await response.json().then((jsn) => {
              return jsn;
            });
            throw new Error(jsn['message']);
          }
          if (response.status != 200) throw new Error(response.statusText);
          else return response.json();
        })
        .then((json) => {
          this.task.output = json;

          if (json.filter((e) => e.label == 'error').length > 0) {
            this.showToaster(
              'Bitte beheben Sie den Fehler im Code !',
              'Fehlerhaft !',
              'danger'
            );
            this.$set(this.task, 'status', 'error');
          } else {
            this.$set(this.task, 'status', 'success');
            this.$emit('resetErrorMessage');
          }

          this.loading = false;
        })
        .catch((e) => {
          this.task.output = [
            {
              label: 'error',
              name: 'error',
              type: 'text',
              value: e,
            },
          ];

          this.showToaster(
            'Bitte beheben Sie den Fehler im Code !',
            'Fehlerhaft !',
            'danger'
          );
          this.loading = false;
        });
    },
    getTSDisplayFormat(ts) {
      const copy = JSON.parse(JSON.stringify(ts));
      const d = copy.data?.slice ? copy.data.slice(0, 2) : [];
      d.push(['...'], ['...']);
      copy.data = d;
      return copy;
    },
    isObjectTimeseries(o) {
      const a = o.hasOwnProperty('ts_type');
      const b = o.hasOwnProperty('supplyPoint');
      const c = o.hasOwnProperty('quantity');
      const d = o.hasOwnProperty('convention');
      return a + b + c + d;
    },
  },
  computed: {
    ...mapGetters({
      databaseList: 'getDatabaseList',
      getUsername: 'getUserId',
    }),
    customCodeDetails() {
      const sourceProps =
        (this.task && this.task.props) || this.editCodeDetails || null;

      if (!sourceProps) return null;
      const clonedProps = JSON.parse(JSON.stringify(sourceProps));
      clonedProps.code = this.taskDetails?.code || clonedProps.code;
      return clonedProps;
    },
    variableInputFields() {
      if (!this.isEditMode) {
        return this.createInputsFromCode(this.taskDetails?.code);
      }
      if (this.customTaskDetails?.inputfields?.length) {
        return this.customTaskDetails.inputfields;
      }
      return [];
    },
    inputAsCode() {
      let code =
        '""" We initiated this dictionary for you based on the input from the previous task. You can not edit this part of the code, but you can use this dictionary in your code."""\n\n';

      if (this.task?.input) {
        this.task.input.forEach((e) => {
          let name = e.name;
          switch (e.type) {
            case 'range':
            case 'text':
            case 'email':
            case 'hidden':
            case 'textarea':
            case 'model':
            case 'date':
            case 'radio':
              if (name != 'codelosSettings')
                code += 'input["' + name + '"] = "' + e.value + '"\n';
              break;
            case 'formatted_number':
            case 'number':
              code += 'input["' + name + '"] = ' + e.value + '\n';
              break;
            case 'timeseries':
            case 'loadprofile':
              code +=
                'input["' +
                name +
                '"] = ' +
                JSON.stringify(this.getTSDisplayFormat(e.value)) +
                '\n';
              break;
            case 'objectlist':
            case 'object':
            case 'custom':
            case 'checkbox':
              code +=
                'input["' + name + '"] = ' + JSON.stringify(e.value) + '\n';
              break;
            case 'file': {
              const fileName = e.value?.name || e.name;
              code +=
                'input["' +
                e.name +
                '"] = {"file_path": "/{exec_path}/' +
                fileName +
                '"}\n';
              break;
            }
            default:
              break;
          }
        });
      }

      return code;
    },
    outputAsCode() {
      let code = '# Results:\n\n';
      if (!this.task?.output) return code;
      this.task.output.forEach((e) => {
        let name = e.name;
        switch (e.type) {
          case 'range':
          case 'text':
          case 'email':
          case 'date':
            if (name != 'codelosSettings')
              code += 'results["' + name + '"] = "' + e.value + '"\n';
            break;
          case 'formatted_number':
          case 'number':
            code += 'results["' + name + '"] = "' + e.value + '"\n';
            break;
          case 'file': {
            let path = e.value.file_path;
            if (path == 'undefined') path = e.name;
            const fileName = path?.split('/')[2] || '';
            code += 'results["' + name + '"]' + ' = {"file": (' + name + false;
            ', open("' +
              getFileNameWithoutExtension(fileName) +
              '", "rb"), "' +
              getFileExtension(fileName) +
              '")}\n';
            break;
          }
          case 'object':
          case 'list':
            if (this.isObjectTimeseries(e.value)) {
              code +=
                'input["' +
                name +
                '"] = ' +
                JSON.stringify(this.getTSDisplayFormat(e.value)) +
                '\n';
              break;
            } else
              code +=
                'results["' + name + '"] = ' + JSON.stringify(e.value) + '\n';
            break;
          case 'error':
            code +=
              '# This error occured while executing the code: \n"' +
              e.value +
              '"\n';
            break;
          default:
            break;
        }
      });
      return code;
    },
    user() {
      return this.$store.state.user;
    },
    getCustomTaskDatabase() {
      return this.databaseList.filter((ele) => ele.type == 'custom-task');
    },
  },
  mounted() {
    this.inputFieldsDetails = this.inputFields;
    if (this.isEditMode) {
      this.setCustomTaskDetails();
    } else {
      this.writeMessage(this.editCodeDetails.code);
      this.taskDetails.code = this.editCodeDetails.code;
    }
  },
  directives: {
    clickOutside: {
      bind(el, binding, vnode) {
        el.eventOnClick = function (event) {
          // Check that the click was outside the el and its children
          if (!(el == event.target || el.contains(event.target))) {
            // call method provided in attribute value
            vnode.context[binding.expression]();
          }
        };

        document.addEventListener('click', el.eventOnClick);
      },
      unbind(el) {
        document.removeEventListener('click', el.eventOnClick);
      },
    },
  },
  components: {
    LoadingSpinner,
    IconPicker,
    PrismEditor,
  },
};
</script>

<style scoped>
.custom-task-edit-component {
  width: 100%;
  height: 100%;
  background: #ffffff;
  position: absolute;
  display: flex;
  flex-direction: column;
  top: 0;
  text-align: left;
  font-family: 'Open Sans', open-sans, sans-serif;
  -webkit-font-smoothing: antialiased;
  font-size: 12px;
  line-height: 17px;
  color: #9f9f9f;
  z-index: 99;
  padding-inline: 34px;
}
.custom-task-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1 1 auto;
}
.code-tool-wrapper {
  display: flex;
  justify-content: flex-end;
  position: relative;
}
.task-tools {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.left-side-options,
.right-side-options {
  display: flex;
  align-items: center;
  list-style: none;
  padding: 0;
  margin: 0;
  gap: 10px;
}
.left-side-options > li > a,
.right-side-options > li > a {
  box-sizing: border-box;
  border: 1px solid #dbdde0;
  border-radius: 50%;
  height: 42px;
  width: 42px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.13);
}
.task-tools i {
  color: #000;
}
.task-tools li > a:hover {
  border-color: #bfc3c9;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.12);
}
.custom-task-wrapper .content {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.accessibility {
  z-index: 30;
  overflow: hidden !important;
  padding: 0 !important;
  position: absolute !important;
  width: 0px !important;
}
.variables-section {
  margin-top: 20px;
  padding: 0;
}
.variables-header h3 {
  padding: 10px 0 10px 0;
  color: #464d5d;
}
.variables-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.variable-row {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}
.variable-name {
  width: 250px;
  font-size: 14px;
  font-weight: 600;
  word-break: break-all;
}
.variable-label-input {
  flex: 1;
  border: 1px solid #dee3e9;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  height: 44px;
}
.custom-code-modal-content-wrapper {
  position: relative;
  z-index: 1;
  height: calc(100% - 42px - 36px);
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}
.custom-task-detail.tab {
  text-align: left;
  padding: 0;
  height: 100%;
}
.code-editors-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.dragable-wrapper {
  position: relative;
}
.dragable-line-first {
  position: absolute;
  display: inline-block;
  width: 100%;
  height: 5px;
  background-color: #fff;
  bottom: 0;
  left: 0;
  right: 0;
  opacity: 0.5;
  cursor: row-resize;
  z-index: 99;
  user-select: none;
}
.dragable-line-second {
  position: absolute;
  display: inline-block;
  width: 100%;
  height: 5px;
  background-color: #fff;
  opacity: 0.5;
  cursor: row-resize;
  z-index: 9;
}
.editor-wrapper.read-only {
  height: 150px;
}
.editor-wrapper {
  margin-top: 20px;
  height: 350px;
  overflow-y: scroll;
  width: 100%;
  border-radius: 4px;
}
.editor-wrapper.read-write .prism-editor-wrapper.code-editor {
  min-height: 450px;
  height: 100%;
  padding-top: 0;
}
::v-deep .prism-editor__container {
  height: fit-content;
  min-height: 450px;
}
.editor-wrapper.hide-overflow {
  overflow-y: hidden;
}
.loading {
  width: 100%;
  height: 100%;
  position: relative;
  z-index: 3;
  opacity: 0.8;
  color: #ffff;
}
.progress-icon-wrapper {
  top: 0;
  bottom: 0;
  height: fit-content;
  width: fit-content;
  left: 0;
  right: 0;
  margin: auto;
  position: absolute;
}
.progress-icon {
  width: 76px;
  height: 76px;
  margin-left: auto;
  top: 0;
  left: 0;
  margin-right: auto;
  margin-top: 0;
}
.progress-icon:after {
  content: ' ';
  display: block;
  width: 70px;
  height: 70px;
  margin: 1px;
  border-radius: 50%;
  border: 2px solid #ffff;
  border-color: #ffff transparent #ffff transparent;
  animation: progress-icon 1.2s linear infinite;
}
@keyframes progress-icon {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
.custom-task-edit-component .content .icon-picker-wrapper {
  display: flex;
}
.custom-task-edit-component .content .icon-picker-wrapper .image-preview img {
  width: 55px;
}
.custom-task-edit-component .content .input-attributes {
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.custom-task-edit-component .content .input-attributes label {
  width: 250px;
  display: block;
  text-align: left;
  padding-right: 15px;
  font-size: 14px;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.custom-task-edit-component
  .content
  .input-attributes
  input:not([type='radio']) {
  width: calc(100% - 250px);
  border: 1px solid #dee3e9;
  border-radius: 0.375rem;
  padding: 10px 15px;
  cursor: text;
  height: auto;
}
.custom-task-edit-component .content .input-attributes textarea {
  resize: none;
  float: left;
  background: #fff;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-right: medium none;
  padding-left: 12px;
  padding-bottom: 15px;
  margin-bottom: 0;
  padding-bottom: 14px;
  padding-top: 15px;
  color: #464d5d;
  border: 1px solid #dee3e9;
  border-radius: 2px;
  font-size: 14px;
  font-weight: 400;
  line-height: 19px;
  margin: 0 0 20px;
  -webkit-font-smoothing: antialiased;
  -webkit-box-shadow: none;
  box-shadow: none;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  height: 200px;
  width: calc(100% - 250px);
  overflow: scroll;
}

.custom-task-edit-component .content .btn-black {
  cursor: pointer;
  text-decoration: none;
  color: rgba(255, 255, 255, 0.951);
  border: 3px solid rgba(0, 0, 0, 0.916);
  background-color: rgba(0, 0, 0, 0.916);
  border-radius: 0.375em;
  font-weight: 400;
  line-height: 23px;
  padding: 4px 20px;
  font-size: 14px;
  margin-bottom: 25px;
  margin-top: 5px;
  letter-spacing: 0.6px;
  text-transform: capitalize;
}
.custom-task-edit-component .content h1 {
  padding-bottom: 8px;
  text-align: center;
  font-size: 33px;
  color: #464d5d;
  margin: 0 0 12px;
  font-weight: 400;
  line-height: 35px;
}
.custom-task-edit-component .content h1 {
  padding-bottom: 8px;
  text-align: center;
  font-size: 33px;
  color: #464d5d;
  margin: 0 0 12px;
  font-weight: 400;
  line-height: 35px;
}

.make-custom-task-wrap {
  height: 100%;
  overflow-y: auto;
  padding-bottom: 40px;
  min-height: 0;
}

.make-custom-task-wrap .input-attributes label {
  width: 250px;
  text-align: left;
  padding-right: 15px;
  font-size: 14px;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: #212529;
}

.make-custom-task-wrap .input-attributes .icon-content-wrap {
  display: flex;
  align-items: center;
  width: calc(100% - 250px);
}
.image-preview {
  background: #ededed;
  border-radius: 6px;
  margin-right: 10px;
  padding: 4px;
  min-height: 50px;
}
.custom-task-edit-component .content .btn-black.submit-btn {
  font-size: 18px;
  padding: 6px 20px;
}
.radio-input-attributes {
  justify-content: flex-start !important;
}
.radio-input-attributes .radio-options {
  display: flex;
  align-items: center;
  width: calc(100% - 250px);
}
.radio-input-attributes .radio-option {
  width: 100px !important;
  text-align: left !important;
  padding-right: 15px;
  font-size: 14px;
  display: flex !important;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: #212529;
  justify-content: center !important;
  align-items: center !important;
  gap: 20px;
}
.radio-input-attributes .radio-option input[type='radio'] {
  width: auto;
  cursor: pointer;
}
.custom-task-edit-component
  .editor-wrapper
  ::v-deep
  .custom-task-edit-component {
  position: relative;
  z-index: 1;
  height: calc(100% - 42px - 36px);
  margin-top: 17px;
}

.custom-task-edit-component
  .editor-wrapper.read-write
  .prism-editor-wrapper.code-editor {
  max-height: 200px;
  height: 100%;
  padding-top: 0;
}
.custom-task-edit-component .editor-wrapper ::v-deep .prism-editor__container {
  min-height: 200px;
}
.custom-code-task-wrapper .modal.task {
  padding: 0 !important;
  padding-inline: 0 !important;
}
</style>
